import React, { createContext, useCallback, useContext, useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { availableWorkspaceCommands, workspaceAccountEligible } from './workspaceCommands';

const WorkspaceContext = createContext(null);
export const useWorkspaceExperience = () => useContext(WorkspaceContext);

export function WorkspaceExperienceProvider({ children }) {
  const auth = useAuth() || {};
  const location = useLocation();
  const scope = JSON.stringify([auth.currentUser?.uid, auth.userData?.companyId]);
  const routeScope = JSON.stringify([scope, location.pathname]);
  const [evidence, setEvidence] = useState({});
  const register = useCallback((id, value) => {
    setEvidence(current => ({ ...current, [id]: value }));
    return () => setEvidence(current => { const next = { ...current }; delete next[id]; return next; });
  }, []);
  const current = Object.values(evidence).filter(row => row.routeScope === routeScope);
  const active = workspaceAccountEligible(auth, location.pathname) && current.length > 0;
  const modules = current.find(row => row.modules)?.modules || null;
  const companyName = current.find(row => row.companyName)?.companyName || '';
  const confirmedModules = current.map(row => row.module).filter(Boolean);
  const commands = active ? availableWorkspaceCommands({ auth, modules, confirmedModules }) : [];
  const value = { register, routeScope, scope, active, modules, commands, companyName };
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// Existing route guards/resolved company readers publish only their current
// successful result. This component performs no reads, writes or authorization.
export function WorkspaceEvidence({ children, ready = true, modules, module, companyName = '' }) {
  const experience = useWorkspaceExperience();
  const id = useId();
  const modulesKey = JSON.stringify(modules || null);
  const register = experience?.register;
  const routeScope = experience?.routeScope;
  useEffect(() => {
    if (!register || !ready) return undefined;
    return register(id, { routeScope, modules: JSON.parse(modulesKey), module, companyName: String(companyName || '') });
  }, [register, routeScope, id, modulesKey, module, companyName, ready]);
  return children || null;
}

export function WorkspaceFrame({ children }) {
  const experience = useWorkspaceExperience();
  return <div className={experience?.active ? 'sbs-experience-frame' : undefined}>{children}</div>;
}
